import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './HealthReports.css';
import '../sales/SalesOrders.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DOC_TYPES = [
  'Lab Report', 'Goal Sheet', 'Diet / Nutrition Plan',
  'Blood Work', 'Fitness Assessment', 'Progress Report',
  'Medical History', 'Other',
];

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/* ── Manage Documents Modal ──────────────────────────────────── */
const ManageDocsModal = ({ order, auth, onClose, onChanged }) => {
  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [busy,      setBusy]      = useState(null); // id of row being saved/deleted

  // New-doc form state
  const [newType,      setNewType]      = useState(DOC_TYPES[0]);
  const [newFile,      setNewFile]      = useState(null);
  const [adding,       setAdding]       = useState(false);
  const newFileRef = useRef(null);

  // Per-row editing state: { [id]: { type, file } }
  const [rowEdit, setRowEdit] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/health-documents`, { params: { order_id: order.id }, ...auth });
      setDocs(r.data || []);
    } catch { setDocs([]); } finally { setLoading(false); }
  }, [order.id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  /* ── Add new document ── */
  const handleAdd = async () => {
    if (!newFile) { setError('Please select a file to upload.'); return; }
    setAdding(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', newFile);
      fd.append('order_id', order.id);
      fd.append('customer_id', order.customer_id || '');
      fd.append('document_type', newType);
      fd.append('document_name', newFile.name);
      await axios.post(`${API_URL}/health-documents/upload`, fd, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      setNewFile(null);
      setNewType(DOC_TYPES[0]);
      if (newFileRef.current) newFileRef.current.value = '';
      await load();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setAdding(false);
    }
  };

  /* ── Save row edits (type and/or file) ── */
  const handleSaveRow = async (doc) => {
    const edit = rowEdit[doc.id] || {};
    const hasTypeChange = edit.type && edit.type !== doc.document_type;
    const hasFileChange = !!edit.file;
    if (!hasTypeChange && !hasFileChange) {
      setRowEdit(prev => { const n = { ...prev }; delete n[doc.id]; return n; });
      return;
    }
    setBusy(doc.id);
    setError('');
    try {
      const fd = new FormData();
      fd.append('document_type', edit.type || doc.document_type);
      if (edit.file) fd.append('file', edit.file);
      await axios.patch(`${API_URL}/health-documents/${doc.id}`, fd, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      setRowEdit(prev => { const n = { ...prev }; delete n[doc.id]; return n; });
      await load();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally {
      setBusy(null);
    }
  };

  /* ── Delete a document ── */
  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.document_name}"?`)) return;
    setBusy(doc.id);
    setError('');
    try {
      await axios.delete(`${API_URL}/health-documents/${doc.id}`, auth);
      await load();
      onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed.');
    } finally {
      setBusy(null);
    }
  };

  const setRowField = (id, field, value) =>
    setRowEdit(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  return (
    <div className="hr-overlay" onClick={onClose}>
      <div className="hr-modal hr-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="hr-modal-header">
          <h3>Manage Documents — {order.order_no}</h3>
          <button className="hr-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="hr-modal-body">
          {error && <div className="hr-modal-error">{error}</div>}

          {/* ── Add new document ── */}
          <div className="hr-add-section">
            <span className="hr-add-label">Add document</span>
            <div className="hr-add-row">
              <select
                className="hr-select hr-add-type"
                value={newType}
                onChange={e => setNewType(e.target.value)}
              >
                {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                ref={newFileRef}
                type="file"
                accept=".pdf,image/*"
                className="hr-file-input hr-add-file"
                onChange={e => setNewFile(e.target.files?.[0] || null)}
              />
              <button
                className="hr-btn hr-btn--save"
                onClick={handleAdd}
                disabled={adding || !newFile}
              >
                {adding ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>

          <div className="hr-divider" />

          {/* ── Existing documents ── */}
          {loading ? (
            <div className="hr-empty">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="hr-empty">No documents uploaded yet.</div>
          ) : (
            <table className="hr-docs-table hr-docs-table--manage">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>File</th>
                  <th>Uploaded</th>
                  <th>View</th>
                  <th>Replace</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => {
                  const edit    = rowEdit[doc.id] || {};
                  const isDirty = !!(edit.type && edit.type !== doc.document_type) || !!edit.file;
                  const isBusy  = busy === doc.id;
                  return (
                    <tr key={doc.id} className={isDirty ? 'hr-row--dirty' : ''}>
                      {/* Editable type */}
                      <td>
                        <select
                          className="hr-select hr-select--sm"
                          value={edit.type ?? doc.document_type}
                          onChange={e => setRowField(doc.id, 'type', e.target.value)}
                          disabled={isBusy}
                        >
                          {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      {/* File name */}
                      <td className="hr-doc-name">
                        {edit.file ? <span className="hr-new-file-badge">{edit.file.name}</span> : doc.document_name}
                      </td>
                      {/* Date */}
                      <td className="hr-doc-date">{formatDate(doc.created_date)}</td>
                      {/* View */}
                      <td>
                        <a className="hr-view-link" href={`${API_URL}${doc.document_url}`} target="_blank" rel="noreferrer">
                          View
                        </a>
                      </td>
                      {/* Replace file */}
                      <td>
                        <label className="hr-replace-btn" title="Replace file">
                          📎
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            style={{ display: 'none' }}
                            onChange={e => setRowField(doc.id, 'file', e.target.files?.[0] || null)}
                            disabled={isBusy}
                          />
                        </label>
                      </td>
                      {/* Save / Delete */}
                      <td>
                        <div className="hr-row-actions">
                          {isDirty && (
                            <button
                              className="hr-btn hr-btn--save hr-btn--xs"
                              onClick={() => handleSaveRow(doc)}
                              disabled={isBusy}
                            >
                              {isBusy ? '…' : 'Save'}
                            </button>
                          )}
                          <button
                            className="hr-btn hr-btn--danger hr-btn--xs"
                            onClick={() => handleDelete(doc)}
                            disabled={isBusy}
                            title="Delete document"
                          >
                            {isBusy && !isDirty ? '…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="hr-modal-footer">
          <button className="hr-btn hr-btn--cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Page ───────────────────────────────────────────────── */
const HealthReports = () => {
  const { user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [customers,            setCustomers]            = useState([]);
  const [customerId,           setCustomerId]           = useState('');
  const [customerInput,        setCustomerInput]        = useState('');
  const [filteredCustomers,    setFilteredCustomers]    = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [orders,               setOrders]               = useState([]);
  const [loadingOrders,        setLoadingOrders]        = useState(false);
  const [manageTarget,         setManageTarget]         = useState(null);
  const [error,                setError]                = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/commonLOVs/lhcustomers`, auth)
      .then(r => setCustomers(r.data || []))
      .catch(() => setCustomers([]));
  }, []); // eslint-disable-line

  const loadOrders = useCallback(async (cid) => {
    if (!cid) { setOrders([]); return; }
    setLoadingOrders(true);
    setError('');
    try {
      const r = await axios.get(`${API_URL}/health-documents/health-orders`, { params: { customer_id: cid }, ...auth });
      setOrders(r.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load orders.');
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, []); // eslint-disable-line

  const handleCustomerInputChange = (e) => {
    const value = e.target.value;
    setCustomerInput(value);
    setCustomerId('');
    if (value.trim() === '') {
      setFilteredCustomers([]);
      setShowCustomerDropdown(false);
      setOrders([]);
    } else {
      const q = value.toLowerCase();
      setFilteredCustomers(
        customers.filter(c =>
          c.customer_name.toLowerCase().includes(q) ||
          (c.phone && c.phone.toLowerCase().includes(q))
        )
      );
      setShowCustomerDropdown(true);
    }
  };

  const handleCustomerSelect = (customer) => {
    setCustomerInput(customer.phone ? `${customer.customer_name} - ${customer.phone}` : customer.customer_name);
    setCustomerId(customer.id);
    setFilteredCustomers([]);
    setShowCustomerDropdown(false);
    loadOrders(customer.id);
  };

  return (
    <div className="hr-page">
      <div className="hr-header">
        <h1 className="hr-title">Health Reports</h1>
        <p className="hr-subtitle">Upload and manage health documents for customer orders</p>
      </div>

      {error && <div className="hr-error">{error}</div>}

      <div className="hr-filter-row">
        <div style={{ position: 'relative', minWidth: 300 }}>
          <input
            type="text"
            className="hr-customer-input"
            placeholder="Search customer by name or phone…"
            value={customerInput}
            onChange={handleCustomerInputChange}
            onFocus={() => { if (customerInput && filteredCustomers.length > 0) setShowCustomerDropdown(true); }}
            onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
            autoComplete="off"
          />
          {showCustomerDropdown && filteredCustomers.length > 0 && (
            <div className="autocomplete-dropdown">
              {filteredCustomers.map(c => (
                <div
                  key={c.id}
                  className="autocomplete-item"
                  onMouseDown={() => handleCustomerSelect(c)}
                >
                  {c.phone ? `${c.customer_name} - ${c.phone}` : c.customer_name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!customerId ? (
        <div className="hr-prompt">Select a customer to view their health orders.</div>
      ) : loadingOrders ? (
        <div className="hr-loading">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="hr-empty-orders">No health orders found for this customer.</div>
      ) : (
        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>Order No</th>
                <th>Date</th>
                <th>Status</th>
                <th className="hr-col-num">Amount</th>
                <th className="hr-col-num">Docs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td className="hr-order-no">{order.order_no}</td>
                  <td>{formatDate(order.order_date)}</td>
                  <td>
                    <span className="hr-status-badge">{order.order_lc_state || order.order_state}</span>
                  </td>
                  <td className="hr-col-num">
                    {order.order_amount != null ? `₹${parseFloat(order.order_amount).toFixed(2)}` : '—'}
                  </td>
                  <td className="hr-col-num">{(order.documents || []).length}</td>
                  <td>
                    <button
                      className="hr-icon-btn hr-icon-btn--manage"
                      onClick={() => setManageTarget(order)}
                    >
                      📄 Documents
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {manageTarget && (
        <ManageDocsModal
          order={manageTarget}
          auth={auth}
          onClose={() => setManageTarget(null)}
          onChanged={() => loadOrders(customerId)}
        />
      )}
    </div>
  );
};

export default HealthReports;
